import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { ContractPicker } from '@/features/contract/components/ContractPicker'
import type { ContractPickerOption } from '@/features/contract/api'
import { useChangeTaskContext } from '../hooks'
import type { Task } from '../types'

interface Props {
  task: Task
  onClose: () => void
}

type ContextKind = 'EASY/CONTRACT' | 'HALO/LEAD' | 'HALO/MEETING'

function kindFromTask(task: Task): ContextKind {
  if (!task.context) return 'EASY/CONTRACT'
  return `${task.context.system}/${task.context.type}` as ContextKind
}

/**
 * Dialog do zmiany kontekstu taska (F2H-B).
 *
 * EASY/CONTRACT -> ContractPicker (searchable po numerze umowy / nazwisku zamawiającego).
 *                  Label budowany z obiektu (code + customer + stage) - klient nic nie edytuje.
 * HALO/LEAD, HALO/MEETING -> graceful fallback: plain ID + label z ostrzeżeniem.
 *                            Label edytowalny - decyzja czy też auto z obiektu jeszcze otwarta.
 *
 * TaskContext invariant: wszystkie 4 pola (system, type, id, label) muszą być
 * niepuste przy zapisie. Null = odepnij task od kontekstu.
 */
export function ChangeContextDialog({ task, onClose }: Props) {
  const changeMutation = useChangeTaskContext()

  const [contextKind, setContextKind] = useState<ContextKind>(kindFromTask(task))

  // Stan dla EASY/CONTRACT (ContractPicker)
  const [selectedContract, setSelectedContract] = useState<ContractPickerOption | null>(
    task.context?.system === 'EASY' && task.context?.type === 'CONTRACT'
      ? {
          id: task.context.id,
          code: task.context.label,
          customerName: '',
          stageName: '',
        }
      : null,
  )

  // Stan dla HALO fallback (plain inputs)
  const [haloId, setHaloId] = useState<string>(
    task.context?.system === 'HALO' ? String(task.context.id) : '',
  )
  const [haloLabel, setHaloLabel] = useState<string>(
    task.context?.system === 'HALO' ? (task.context.label ?? '') : '',
  )

  const [error, setError] = useState<string | null>(null)

  const handleKindChange = (v: ContextKind | null) => {
    if (!v) return
    setContextKind(v)
    setSelectedContract(null)
    setHaloId('')
    setHaloLabel('')
    setError(null)
  }

  const handleSave = () => {
    setError(null)

    if (contextKind === 'EASY/CONTRACT') {
      if (!selectedContract) {
        setError('Wybierz umowę z listy.')
        return
      }
      // Label snapshot - format spojny z ContractDetailPage:
      // "Umowa <code> - <customer>". Stage celowo pominiety - zmienia sie w czasie,
      // a label powinien byc stabilny (pokazuje co byl task'iem zaczepiony, nie co dzieje
      // sie z umowa teraz).
      const customer = selectedContract.customerName?.trim()
      const label = customer
        ? `Umowa ${selectedContract.code} - ${customer}`
        : `Umowa ${selectedContract.code}`
      changeMutation.mutate(
        {
          taskId: task.id,
          context: {
            system: 'EASY',
            type: 'CONTRACT',
            id: selectedContract.id,
            label,
          },
        },
        { onSuccess: onClose, onError: () => setError('Nie udało się zmienić kontekstu.') },
      )
      return
    }

    // HALO/LEAD lub HALO/MEETING - plain inputs
    const idNum = Number(haloId)
    if (!haloId.trim() || isNaN(idNum) || idNum <= 0) {
      setError('Podaj poprawne ID dokumentu (liczba > 0).')
      return
    }
    if (!haloLabel.trim()) {
      setError('Podaj etykietę kontekstu.')
      return
    }
    const [system, type] = contextKind.split('/') as ['HALO', 'LEAD' | 'MEETING']
    changeMutation.mutate(
      { taskId: task.id, context: { system, type, id: idNum, label: haloLabel.trim() } },
      { onSuccess: onClose, onError: () => setError('Nie udało się zmienić kontekstu.') },
    )
  }

  const handleDetach = () => {
    setError(null)
    changeMutation.mutate(
      { taskId: task.id, context: null },
      { onSuccess: onClose, onError: () => setError('Nie udało się odpiąć kontekstu.') },
    )
  }

  const isHaloFallback = contextKind === 'HALO/LEAD' || contextKind === 'HALO/MEETING'

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[440px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">
            {task.context ? 'Zmień kontekst' : 'Przypisz kontekst'}
          </Dialog.Title>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            {/* Krok 1: wybor rodzaju kontekstu */}
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Typ kontekstu</span>
              <Combobox<ContextKind>
                value={contextKind}
                onChange={handleKindChange}
                options={[
                  { value: 'EASY/CONTRACT', label: 'Umowa easycrm' },
                  { value: 'HALO/LEAD', label: 'Lead (HaloCRM)' },
                  { value: 'HALO/MEETING', label: 'Spotkanie (HaloCRM)' },
                ]}
                placeholder="Wybierz typ kontekstu..."
              />
            </div>

            {/* Krok 2: picker zalezny od rodzaju */}
            {contextKind === 'EASY/CONTRACT' && (
              <div className="grid gap-1">
                <span className="text-xs uppercase text-slate-500">Umowa</span>
                <ContractPicker value={selectedContract} onChange={setSelectedContract} />
              </div>
            )}

            {isHaloFallback && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Wyszukiwanie HALO leadów/spotkań w przygotowaniu. Wpisz ID i etykietę ręcznie.
                {/* TODO: HaloLeadPicker - follow-up */}
              </div>
            )}

            {isHaloFallback && (
              <>
                <label className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">ID dokumentu</span>
                  <input
                    type="number"
                    min={1}
                    value={haloId}
                    onChange={(e) => setHaloId(e.target.value)}
                    placeholder="np. 123"
                    className="border rounded px-2 py-1"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">Etykieta (czytelna nazwa)</span>
                  <input
                    type="text"
                    value={haloLabel}
                    onChange={(e) => setHaloLabel(e.target.value)}
                    placeholder="np. Kowalski 10kW - spotkanie"
                    className="border rounded px-2 py-1"
                  />
                </label>
              </>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex items-center justify-between gap-2 mt-2">
              <div>
                {task.context && (
                  <button
                    type="button"
                    onClick={handleDetach}
                    disabled={changeMutation.isPending}
                    className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Odepnij
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1 text-sm border rounded"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={changeMutation.isPending}
                  className="px-3 py-1 text-sm bg-sky-600 text-white rounded disabled:opacity-50"
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
